import { Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { OrderService } from '../../core/services/order.service';
import { ConfigService } from '../../core/services/config.service';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { LayoutService } from '../../core/services/layout.service';
import { OrderType } from '../../core/models/order-type.enum';

@Component({
  selector: 'cwo-order-details-button',
  standalone: true,
  imports: [IconComponent, CommonModule],
  templateUrl: './order-details-button.component.html',
  styleUrl: './order-details-button.component.scss',
})
export class OrderDetailsButtonComponent {
  isInCart = input<boolean>(false);
  storeInfoStyle = input<boolean>(false);
  iconName = IconNameType;
  private layout = inject(LayoutService);
  private modalService = inject(ModalService);
  private orderService = inject(OrderService);
  private configService = inject(ConfigService);
  private order = computed(() => {
    return this.orderService.order();
  });

  orderDetailsButtonLabel = computed(() => {
    if (!this.order().dummy && !this.isInCart()) {
      return this.orderType();
    } else if (!this.order().dummy && this.isInCart()) {
      return `${this.orderType()} • ${this.orderDetailsSubtitle()}`;
    } else {
      return 'Order Details';
    }
  });

  orderType = computed(() => {
    return (
      this.configService.findOrderTypeConfig(this.order().orderType)
        ?.displayName ?? this.order().orderType
    );
  });

  hasOrderType = computed(() => {
    if (!this.order().dummy) {
      return true;
    } else {
      return false;
    }
  });

  isSelfOrder = computed(() => !this.order().dummy && this.order().isSelfOrderSource);

  orderDetailsSubtitle = computed(() => {
    const pre = this.orderDetailsPreposition();
    const addr = this.orderDetailsAddress();
    if (!addr) return '';
    return `${pre} ${this.truncateAddress(addr, 26)}`;
  });

  private orderDetailsPreposition = computed(() => {
    if (!this.order || this.order().dummy) return '';
    if (this.order().orderType !== OrderType.Delivery) return 'From';
    return 'To';
  });

  private orderDetailsAddress = computed(() => {
    const storeAddress = this.configService.configState()?.store?.address;
    if (!this.order || this.order().dummy) return '';
    const type = (this.order().orderType)
    if (type === OrderType.Delivery) {
      return this.order()?.customer?.address?.street;
    }
    // PICKUP and CURBSIDE show the store address
    return storeAddress;
  });

  private truncateAddress(s: string, max: number) {
    let numChars = max;
    (window.innerWidth < 390) ? numChars = max/2 : null;
    return s.length > numChars ? s.slice(0, numChars - 1).trimEnd() + '…' : s;
  }

  toggleOrderDetails() {
    if (this.isSelfOrder()) return;

    const openOrderDetailsModal = () => {
      this.modalService.openModal(ModalName.OrderDetails, {
        showOrderEditor:
          this.layout.mobileView() || this.orderService.order().dummy,
      });
    };

    if (!this.isInCart()) {
      this.modalService.closeAllModals().subscribe(() => {
        openOrderDetailsModal();
      });
    } else {
      openOrderDetailsModal();
    }
  }
}
